import React from "react";
import { useNavigate } from "react-router-dom";

import { DATE_FORMATS, DateTime } from "@bps/date-time";
import {
  FontIcon,
  IColumn,
  IconButton,
  mergeStyles,
  ScrollablePane,
  ShimmeredDetailsListProps,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { CopyableGuid } from "@components/CopyableText";
import { DetailsRowExpand } from "@components/DetailsRowExpand";
import { ShimmeredDetailsList } from "@components/tables/ShimmeredDetailsList";
import { BpIdUserDto } from "@libs/api/gateways/bp-id/bp-id.dtos";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

import { UsersTabs } from "../users-tabs.constants";
import { BusinessUserCard } from "./BusinessUserCard";

const getSignInTypeId = (
  signInIdentities: BpIdUserDto["signInIdentities"],
  signInType: string
) => {
  return (
    signInIdentities?.find(x => x.signInType === signInType)
      ?.issuerAssignedId ?? "-"
  );
};

const DEFAULT_DISPLAY_NAME = "unknown";

export const BusinessUsersTable: React.FC<ShimmeredDetailsListProps> = props => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: hasWritePermission } = usePermissionsCheckQuery(
    [
      Permissions.PltBpidUserWrite,
      Permissions.PltBpidUserClear,
      Permissions.PltBpidMfaDelete
    ],
    "or"
  );

  const columns: IColumn[] = [
    {
      key: "actions",
      name: "",
      minWidth: 34,
      maxWidth: 34,
      onRender: (item: BpIdUserDto) => (
        <IconButton
          iconProps={{ iconName: hasWritePermission ? "Edit" : "View" }}
          title={hasWritePermission ? "View & edit account" : "View account"}
          onClick={() =>
            navigate(
              `/users/${UsersTabs.BusinessUsers.IdentitySearch.id}/${item.id}`
            )
          }
        />
      )
    },
    {
      key: "id",
      name: "Object ID",
      minWidth: 100,
      maxWidth: 150,
      onRender: ({ id }) => (id ? <CopyableGuid short value={id} /> : "")
    },
    {
      key: "name",
      name: "Name",
      fieldName: "displayName",
      minWidth: 100,
      maxWidth: 350,
      onRender: ({ displayName, firstName, lastName }: BpIdUserDto) =>
        displayName && displayName !== DEFAULT_DISPLAY_NAME
          ? displayName
          : `${firstName} ${lastName}`
    },
    {
      key: "email",
      name: "Username",
      minWidth: 100,
      maxWidth: 350,
      onRender: ({ signInIdentities }) => (
        <Text nowrap>{getSignInTypeId(signInIdentities, "emailAddress")}</Text>
      )
    },
    {
      key: "createdDateTime",
      name: "Created",
      minWidth: 200,
      maxWidth: 200,
      onRender: ({ createdDate }: BpIdUserDto) =>
        createdDate &&
        DateTime.fromISO(createdDate).toFormat(DATE_FORMATS.DETAILED_DATE_TIME)
    },
    {
      key: "accountEnabled",
      name: "Cloud password",
      minWidth: 100,
      maxWidth: 100,
      onRender: ({ accountEnabled }) => (
        <FontIcon
          iconName={accountEnabled ? "Completed" : "Cancel"}
          className={mergeStyles({
            padding: 1,
            borderStyle: "none",
            color: accountEnabled
              ? theme.palette.green
              : theme.palette.neutralSecondary
          })}
        />
      )
    }
  ];
  return (
    <Stack styles={{ root: { position: "relative", height: "100%" } }}>
      <ScrollablePane>
        <ShimmeredDetailsList
          stickyHeader
          columns={columns}
          onRenderRow={row => (
            <DetailsRowExpand
              row={row}
              expand={<BusinessUserCard identity={row?.item} />}
            />
          )}
          {...props}
        />
      </ScrollablePane>
    </Stack>
  );
};
